import * as React from "react";
import { useLocalization } from "@shared-ui/localization-context";
import { ViewLarge, Viewport, ViewSmall } from "@shared-ui/viewport-context";

import { UitkHeading } from "uitk-react-text";
import { UitkLayoutGrid } from "uitk-react-layout-grid";
import { AnalyticsConfig } from "uitk-react-utils";

import VisitorBlogCard from "./components/VisitorBlogCard";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";

import { VisitorBlogsProps } from "./typings";
import { Article as VisitorBlog, TeArticleFlexModuleResult } from "typings/microserviceModels/te-article-flex-module";
import { withStores } from "stores";
import IsomorphicCarousel from "src/components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { observer } from "mobx-react";
import { UitkSpacing } from "uitk-react-spacing";

const VisitorBlogs = withStores(
  "flexModuleModelStore",
  "analytics"
)(
  observer((props: VisitorBlogsProps) => {
    const { flexModuleModelStore, context, templateComponent } = props;

    const {
      metadata: { id },
      config: { fmId, fmTitleId },
    } = templateComponent;
    const model = flexModuleModelStore.getModel(id) as TeArticleFlexModuleResult | null;

    if (!model) {
      return null;
    }
    const { content, title } = model;

    if (!content?.items?.length) {
      return null;
    }

    const { items: visitorBlogs } = content;
    const locationName = context.searchContext.location?.name;
    const { formatText } = useLocalization();

    const localisedHeading = title || (locationName && formatText("visitorBlogs.title", locationName));

    /* istanbul ignore next */
    const analytics: AnalyticsConfig = {
      id: "TG.LP.Dest.Articles",
      callback: (moduleId, description) => {
        const flexTrackingInfo: FlexTrackingInfo = {
          moduleName: moduleId,
          action: Action.SCROLL,
          linkName: description,
        };
        sendDelayedTrackEvent(flexTrackingInfo, props.analytics);
      },
    };

    const visitorBlogComponentArray = () => {
      const articleKeyHelper = new ItemKeyHelper("top-articles");

      return visitorBlogs.map((visitorBlog: VisitorBlog) => (
        <VisitorBlogCard
          key={articleKeyHelper.next()}
          visitorBlog={visitorBlog}
          visitorBlogImageUrl={visitorBlog.media?.mediaUrl}
        />
      ));
    };

    return (
      <Viewport>
        <ViewSmall>
          <div
            id={id}
            data-fm={fmId}
            data-fm-title-id={fmTitleId}
            className={`VisitorBlogs${visitorBlogs.length > 1 ? " VisitorBlogs-carousel" : ""}`}
          >
            <UitkSpacing padding={{ inlinestart: "three" }} margin={{ blockend: "three" }}>
              <UitkHeading tag="h3" size={4}>
                {localisedHeading}
              </UitkHeading>
            </UitkSpacing>
            <UitkSpacing padding={{ inlinestart: "three" }}>
              <IsomorphicCarousel
                analytics={analytics}
                carouselName="Visitor Blogs"
                itemsVisible={{ lg: 1, md: 1, sm: 1 }}
                pageBy={1}
                peek
                buttonText={{
                  nextButton: formatText("carousel.item.next"),
                  prevButton: formatText("carousel.item.prev"),
                }}
              >
                {visitorBlogComponentArray()}
              </IsomorphicCarousel>
            </UitkSpacing>
          </div>
        </ViewSmall>

        <ViewLarge>
          <div id={id} data-fm={fmId} data-fm-title-id={fmTitleId} className="VisitorBlogs">
            <UitkHeading tag="h3" size={4}>
              {localisedHeading}
            </UitkHeading>
            <UitkSpacing margin={{ blockstart: "three" }}>
              <UitkLayoutGrid columns={3} space="three">
                {visitorBlogComponentArray()}
              </UitkLayoutGrid>
            </UitkSpacing>
          </div>
        </ViewLarge>
      </Viewport>
    );
  })
);

export default VisitorBlogs;
